import { API_ENDPOINTS } from '@/shared/api/endPoints';

export interface SendPhoneClickPayload {
  phone_number: string;
  browser: string;
  timestamp: string; // RFC3339 format with timezone, e.g. "2024-01-15T10:30:00+05:00"
}

/**
 * Отправляет данные о клике по номеру телефона
 * @param clickData - данные клика (номер телефона, браузер, время с таймзоной)
 * @returns Promise с результатом отправки
 *
 * @example
 * ```typescript
 * import sendPhoneClick, { getCurrentTimestampWithTimezone } from '@/shared/api/sendPhoneClick';
 *
 * await sendPhoneClick({
 *   phone_number: "+79991234567",
 *   browser: navigator.userAgent,
 *   timestamp: getCurrentTimestampWithTimezone()
 * });
 * ```
 */
export default async function sendPhoneClick(clickData: SendPhoneClickPayload) {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}${API_ENDPOINTS.CLICK}`,
    {
      method: 'POST',
      body: JSON.stringify(clickData),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to send phone click: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Вспомогательная функция для получения текущего времени в формате RFC3339 с таймзоной пользователя
 * @returns строка с датой и временем в формате RFC3339 (например: "2024-01-15T15:30:45+03:00")
 */
export function getCurrentTimestampWithTimezone(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${getTimezoneOffset()}`;
}

/**
 * Получает смещение таймзоны в формате +05:00 или -03:00
 */
function getTimezoneOffset(): string {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
